/**
 * SitePick front — 「前回見たサイト」（docs/06 §10）
 *
 * 記録: 商品詳細（`data-sp-page="detail"`）と実物確認（`/live/`）の `<main data-sp-recent-id>` を開いたら、
 *   localStorage `sitepick_recent` に商品 post ID と閲覧日時を積む（新しい順・上限 MAX_ITEMS）。
 * 訪問の区切り: どのページでも、前回の操作から VISIT_GAP_MS 以上あいていたら「新しい訪問」とみなし、
 *   その時刻を visitStart に置く（タブをまたいでも同じ区切りになるよう localStorage に持つ）。
 * 表示: `[data-sp-recent]` がある一覧・トップで、visitStart より前に見たサイト（= 前回までの訪問）だけを
 *   最大 SHOW_MAX 件、`POST /sites/batch` の最新データで描画する。無ければ枠は隠れたまま。
 *
 * 保存するのは ID と日時だけ（氏名・入力内容は持たない）。サーバーへ送るのは表示用の ID だけ（お気に入りと同じ）。
 * 価格・在庫の判断はしない（状態の文言はサーバーの status をそのまま言い換えるだけ）。
 */

import { readJSON, writeJSON } from "./storage.js";
import { fetchBatchSites } from "./site-api.js";
import { siteToItem, viewItemList } from "./analytics.js";

const KEY = "sitepick_recent";
const MAX_ITEMS = 12;
const SHOW_MAX = 4;
const VISIT_GAP_MS = 30 * 60 * 1000;
const LIST_ID = "recently_viewed";

/**
 * @returns {{visitStart:number,lastActive:number,items:Array<{id:number,t:number}>}}
 */
function load() {
  const raw = readJSON(KEY, null);
  const out = { visitStart: 0, lastActive: 0, items: [] };
  if (!raw || typeof raw !== "object") {
    return out;
  }
  out.visitStart = Number.isFinite(raw.visitStart) ? raw.visitStart : 0;
  out.lastActive = Number.isFinite(raw.lastActive) ? raw.lastActive : 0;
  if (Array.isArray(raw.items)) {
    const seen = new Set();
    raw.items.forEach((it) => {
      const id = it && Number.isInteger(it.id) && it.id > 0 ? it.id : 0;
      const t = it && Number.isFinite(it.t) ? it.t : 0;
      if (id && t && !seen.has(id) && out.items.length < MAX_ITEMS) {
        seen.add(id);
        out.items.push({ id, t });
      }
    });
  }
  return out;
}

/**
 * 訪問の区切りを更新し、このページが商品なら記録する。
 *
 * @param {number} now
 * @returns {{visitStart:number,lastActive:number,items:Array<{id:number,t:number}>}}
 */
function touch(now) {
  const state = load();
  if (!state.lastActive || now - state.lastActive >= VISIT_GAP_MS || state.visitStart > now) {
    state.visitStart = now;
  }
  state.lastActive = now;

  const holder = document.querySelector("main[data-sp-recent-id]");
  const id = holder ? parseInt(holder.getAttribute("data-sp-recent-id") || "", 10) : 0;
  if (Number.isInteger(id) && id > 0) {
    state.items = [{ id, t: now }].concat(state.items.filter((it) => it.id !== id)).slice(0, MAX_ITEMS);
  }
  writeJSON(KEY, state);
  return state;
}

function msg(key, fallback) {
  const m = (window.SITEPICK && window.SITEPICK.messages) || {};
  return typeof m[key] === "string" && m[key] ? m[key] : fallback;
}

function statusNote(site) {
  switch (site.status) {
    case "SOLD":
      return msg("statusSold", "SOLD");
    case "RESERVED":
      return msg("statusReserved", "手続き中");
    case "HELD":
      return msg("statusHeld", "取置き中");
    default:
      return "";
  }
}

/**
 * 1 件分。リンクは商品詳細（選んだ理由を読み直せる場所）へ。
 *
 * @param {Object} site
 * @param {number} index
 * @returns {HTMLLIElement}
 */
function renderItem(site, index) {
  const li = document.createElement("li");
  li.className = "p-recent__item";
  const item = siteToItem(site, index);
  item.item_list_id = LIST_ID;
  item.item_list_name = msg("recentListName", "前回見たサイト");
  try {
    li.setAttribute("data-sp-item", JSON.stringify(item));
  } catch {
    /* optional */
  }

  const link = document.createElement("a");
  link.className = "p-recent__link";
  link.href = site.url || "#";

  const media = document.createElement("span");
  media.className = "p-recent__media";
  if (site.thumb) {
    const img = document.createElement("img");
    img.className = "p-recent__img";
    img.src = site.thumb;
    img.alt = "";
    img.loading = "lazy";
    img.decoding = "async";
    media.appendChild(img);
  }
  link.appendChild(media);

  const name = document.createElement("span");
  name.className = "p-recent__name";
  name.textContent = site.name || "";
  link.appendChild(name);

  const note = statusNote(site);
  if (note) {
    const badge = document.createElement("span");
    badge.className = `p-recent__status is-status-${String(site.status).toLowerCase()}`;
    badge.textContent = note;
    link.appendChild(badge);
  }

  li.appendChild(link);
  return li;
}

/**
 * @param {{visitStart:number,items:Array<{id:number,t:number}>}} state
 */
async function show(state) {
  const box = document.querySelector("[data-sp-recent]");
  const list = box ? box.querySelector("[data-sp-recent-list]") : null;
  if (!box || !list) {
    return;
  }
  const ids = state.items.filter((it) => it.t < state.visitStart).map((it) => it.id).slice(0, SHOW_MAX);
  if (ids.length === 0) {
    return;
  }
  let sites;
  try {
    sites = await fetchBatchSites(ids);
  } catch {
    return; // 取れなければ出さない（本文には影響しない）
  }
  const byId = new Map();
  (Array.isArray(sites) ? sites : []).forEach((s) => {
    if (s && Number.isInteger(s.id) && s.status && s.status !== "DRAFT") {
      byId.set(s.id, s);
    }
  });
  const ordered = ids.filter((id) => byId.has(id)).map((id) => byId.get(id));
  if (ordered.length === 0) {
    return;
  }
  list.textContent = "";
  ordered.forEach((site, i) => list.appendChild(renderItem(site, i + 1)));
  box.hidden = false;
  box.classList.add("is-active");
  viewItemList(
    LIST_ID,
    msg("recentListName", "前回見たサイト"),
    ordered.map((s, i) => Object.assign(siteToItem(s, i + 1), { item_list_id: LIST_ID }))
  );
}

/**
 * 起動。全ページで訪問の区切りを更新し、枠があるページだけ描画する。
 *
 * @returns {void}
 */
export function initRecentSites() {
  const state = touch(Date.now());
  show(state);
}
